/**
 * General-US regulatory feasibility screen: vocabulary and strict contracts.
 *
 * The screen answers six early-gate questions per project: data-center land
 * use, generation land use, the air permit and operating duty, ownership and
 * electricity sale, the grid interface per connection scenario, and grid
 * service/economics. Each answer is one of exactly four statuses:
 *
 *   - 'no blocker identified'  — a named, checked, effective source shows no
 *     blocker. It ALWAYS carries {@link NO_BLOCKER_QUALIFIER}: it is never an
 *     approval or legal clearance.
 *   - 'approval required'      — an identified authority must approve, and
 *     nothing evidenced blocks the request.
 *   - 'unresolved'             — missing, unknown or unevidenced input. It
 *     carries a reason code: 'not-yet-screened' (nothing to screen) or 'open'
 *     (inputs were screened and the question is still open).
 *   - 'confirmed restriction'  — ONLY from a document or provider basis with
 *     an effective rule. A proposed rule, a proceeding, a declaration or a
 *     zoning label is never a confirmed restriction.
 *
 * Doctrine the schemas enforce (owner guidance, 5 October 2026):
 *   - islanding never exempts land-use, air, fire/building, water, fuel or
 *     electricity-sale requirements;
 *   - zero export does not by itself remove interconnection review;
 *   - generator interconnection is not load service, and a utility indication
 *     or unknown grid capacity is never an entitlement;
 *   - emergency approval is not prime-power permission, and no reciprocating
 *     engine emergency-hour allowance is transferred to turbines;
 *   - there is no MW safe harbour: no status depends on a MW figure, and no
 *     issue text states one;
 *   - FERC's 18 June 2026 large-load actions are proceedings only.
 *
 * Pure contracts only: no IO, no SED access, no provider calls.
 */
import { z } from 'zod';

// ── Frozen v1 copy ───────────────────────────────────────────────────────────
// The exact strings every 'regulatory-feasibility-screen-v1' result carries.
// NEVER edit a *_V1 constant (a hash pin test fails on any edit): stored v1
// records keep them forever. New copy gets new constants and a version bump;
// the live names below then point at the new version's strings.

export const REGULATORY_FEASIBILITY_SCREEN_VERSION_V1 = 'regulatory-feasibility-screen-v1' as const;
export const SOURCES_CHECKED_ON_V1 = '2026-10-05' as const;
export const NO_BLOCKER_QUALIFIER_V1 = 'No blocker identified is not an approval or legal clearance.' as const;
export const REGULATORY_SCREEN_QUALIFIER_V1 = 'Evidence-qualified screening statuses. None is a permit, approval or legal clearance; sources were checked on 5 October 2026 and must be reverified at report generation.' as const;
export const REGULATORY_DOCTRINE_V1 = {
  islandedNoExemption: 'Islanded operation does not exempt the project from land-use, air, fire/building, water, fuel or electricity-sale requirements.',
  zeroExportStillReviewed: 'Zero export does not by itself remove utility interconnection review.',
  generatorInterconnectionNotLoadService: 'Generator interconnection is not load service.',
  unknownGridRightsNotEntitlement: 'A utility indication or unknown grid capacity is not an entitlement to service.',
  emergencyNotPrime: 'Emergency approval is not prime-power permission.',
  noMwSafeHarbour: 'Planned MW alone does not set air-permit applicability; no MW figure is a safe harbour.',
  btmCanBeGridParallel: 'Behind-the-meter describes the meter arrangement; it can run islanded or grid-parallel.',
  fercLargeLoadProceeding: 'FERC large-load and co-location actions of 18 June 2026 are proceedings, not approvals.',
} as const;

// ── Live copy: the strings NEW results carry ─────────────────────────────────

export const REGULATORY_FEASIBILITY_SCREEN_VERSION = REGULATORY_FEASIBILITY_SCREEN_VERSION_V1;
/** Date the owner-supplied doctrine sources were last checked (sources.ts). */
export const SOURCES_CHECKED_ON = SOURCES_CHECKED_ON_V1;

// ── Status vocabulary ────────────────────────────────────────────────────────

export const REGULATORY_ISSUE_STATUSES = [
  'no-blocker-identified', 'approval-required', 'unresolved', 'confirmed-restriction',
] as const;
export type RegulatoryIssueStatus = typeof REGULATORY_ISSUE_STATUSES[number];

/** The owner's four display labels, exactly. */
export const REGULATORY_ISSUE_STATUS_LABEL: Readonly<Record<RegulatoryIssueStatus, string>> = {
  'no-blocker-identified': 'no blocker identified',
  'approval-required': 'approval required',
  unresolved: 'unresolved',
  'confirmed-restriction': 'confirmed restriction',
};

/** Carried by every 'no blocker identified' row. */
export const NO_BLOCKER_QUALIFIER = NO_BLOCKER_QUALIFIER_V1;

export const UNRESOLVED_REASONS = ['not-yet-screened', 'open'] as const;
export type UnresolvedReason = typeof UNRESOLVED_REASONS[number];
export const UNRESOLVED_REASON_LABEL: Readonly<Record<UnresolvedReason, string>> = {
  'not-yet-screened': 'not yet screened',
  open: 'screened, still open',
};

/** Screen-level qualifier carried by every frozen result. */
export const REGULATORY_SCREEN_QUALIFIER = REGULATORY_SCREEN_QUALIFIER_V1;

// ── Issues, scenarios, authorities, sources ─────────────────────────────────

export const REGULATORY_ISSUE_IDS = [
  'land-use-data-center',
  'land-use-generation',
  'air-permit-operating-duty',
  'ownership-electricity-sale',
  'grid-interface',
  'grid-service-economics',
] as const;
export type RegulatoryIssueId = typeof REGULATORY_ISSUE_IDS[number];
export const REGULATORY_ISSUE_LABEL: Readonly<Record<RegulatoryIssueId, string>> = {
  'land-use-data-center': 'Land use: data-center use',
  'land-use-generation': 'Land use: on-site generation use',
  'air-permit-operating-duty': 'Air permit and operating duty',
  'ownership-electricity-sale': 'Ownership and electricity sale',
  'grid-interface': 'Grid interface',
  'grid-service-economics': 'Grid service and economics',
};

/** Connection scenario of a phase. BTM is a meter arrangement and appears in
 * both the islanded and the grid-parallel scenarios. */
export const CONNECTION_SCENARIOS = ['islanded', 'grid-parallel-non-export', 'grid-parallel-export', 'unknown'] as const;
export type ConnectionScenario = typeof CONNECTION_SCENARIOS[number];
export const CONNECTION_SCENARIO_LABEL: Readonly<Record<ConnectionScenario, string>> = {
  islanded: 'Islanded (no grid connection)',
  'grid-parallel-non-export': 'Grid-parallel, non-export',
  'grid-parallel-export': 'Grid-parallel, exporting',
  unknown: 'Connection mode unknown',
};

export const AUTHORITY_LEVELS = ['local', 'state', 'federal', 'utility', 'rto-iso', 'unknown'] as const;
export type AuthorityLevel = typeof AUTHORITY_LEVELS[number];

/** Status of the rule behind a site source. Only 'effective' can support a
 * 'no blocker identified' or 'confirmed restriction' status. */
export const SOURCE_RULE_STATUSES = ['effective', 'proposed', 'proceeding', 'unidentified', 'unknown'] as const;
export type SourceRuleStatus = typeof SOURCE_RULE_STATUSES[number];

export const EVIDENCE_BASES = ['document', 'provider', 'declared', 'none'] as const;
export type EvidenceBasis = typeof EVIDENCE_BASES[number];

export const PERMIT_STAGES = ['construction', 'operating', 'unknown'] as const;
export type PermitStage = typeof PERMIT_STAGES[number];

/** Mirrors `ProjectPhase.generationDuty` (siteRag.ts); absent reads 'unknown'. */
export const GENERATION_DUTIES = ['emergency-standby', 'peaking', 'prime-continuous', 'mixed', 'unknown'] as const;
export type GenerationDuty = typeof GENERATION_DUTIES[number];

/** Doctrine sentences the screen must carry where they apply. */
export const REGULATORY_DOCTRINE = REGULATORY_DOCTRINE_V1;

/** Issue text never states a MW figure or an hour allowance. */
const FORBIDDEN_ISSUE_TEXT: ReadonlyArray<{ pattern: RegExp; message: string }> = [
  { pattern: /\b\d[\d,]*(?:\.\d+)?\s*(?:[kMG]W|MVA)\b/, message: 'Issue text must not state a MW threshold or figure.' },
  { pattern: /\b\d[\d,]*\s*-?\s*(?:hours?|hrs?)\b/i, message: 'Issue text must not state an operating-hour allowance.' },
];

const FERC_JUNE_2026 = /\bFERC\b[\s\S]*(?:2026-06-18|18 June 2026|June 18,? 2026)|(?:2026-06-18|18 June 2026|June 18,? 2026)[\s\S]*\bFERC\b/i;

const nonEmpty = z.string().trim().min(1);
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}(?:T[\d:.]+(?:Z|[+-]\d{2}:\d{2}))?$/);

/** Shape of a source, without doctrine (stored records are parsed with it). */
const regulatorySourceShape = z.object({
  id: nonEmpty,
  label: nonEmpty,
  url: z.string().url().optional(),
  /** Date the source states or was retrieved; null when not recorded. */
  asOf: isoDate.nullable(),
  ruleStatus: z.enum(SOURCE_RULE_STATUSES),
}).strict();

function fercProceedingViolation(value: z.infer<typeof regulatorySourceShape>): boolean {
  return FERC_JUNE_2026.test(`${value.id} ${value.label}`) && value.ruleStatus !== 'proceeding';
}
const FERC_PROCEEDING_MESSAGE = 'FERC 18 June 2026 large-load actions are proceedings only.';

export const regulatorySourceSchema = regulatorySourceShape.superRefine((value, context) => {
  if (fercProceedingViolation(value)) context.addIssue({ code: 'custom', message: FERC_PROCEEDING_MESSAGE });
});
export type RegulatorySource = z.infer<typeof regulatorySourceShape>;

export const regulatoryAuthoritySchema = z.object({
  level: z.enum(AUTHORITY_LEVELS),
  name: nonEmpty.nullable(),
}).strict();
export type RegulatoryAuthority = z.infer<typeof regulatoryAuthoritySchema>;

// ── Issue rows ───────────────────────────────────────────────────────────────

const ISLANDED_DOCTRINE_ISSUES: ReadonlySet<RegulatoryIssueId> = new Set([
  'land-use-data-center', 'land-use-generation', 'air-permit-operating-duty', 'ownership-electricity-sale',
]);

/** Shape of an issue row, without copy literals or doctrine (stored records
 * are parsed with it; their integrity is their hashes). */
const regulatoryIssueShape = z.object({
  /** Unique row id: the issue id, plus the scenario for grid-interface rows. */
  id: nonEmpty,
  issue: z.enum(REGULATORY_ISSUE_IDS),
  status: z.enum(REGULATORY_ISSUE_STATUSES),
  unresolvedReason: z.enum(UNRESOLVED_REASONS).optional(),
  qualifier: nonEmpty.optional(),
  affectedScenarios: z.array(z.enum(CONNECTION_SCENARIOS)).min(1),
  /** Project phases the row applies to; empty when no phase is recorded. */
  phaseIds: z.array(nonEmpty),
  authority: regulatoryAuthoritySchema,
  evidenceBasis: z.enum(EVIDENCE_BASES),
  sources: z.array(regulatorySourceShape),
  /** Air row only. */
  permitStage: z.enum(PERMIT_STAGES).optional(),
  /** Air row only. */
  duty: z.enum(GENERATION_DUTIES).optional(),
  implication: nonEmpty,
  nextAction: nonEmpty,
  reverifyAtReport: z.literal(true),
}).strict();

/** A NEWLY produced row: the shape plus every doctrine invariant and the
 * live copy. Never used to re-judge a stored record. */
export const regulatoryIssueSchema = regulatoryIssueShape.superRefine((row, context) => {
  const fail = (message: string) => context.addIssue({ code: 'custom', message: `${row.id}: ${message}` });
  if (row.qualifier !== undefined && row.qualifier !== NO_BLOCKER_QUALIFIER) fail('The no-blocker qualifier must be the exact not-an-approval qualifier.');
  if (row.sources.some(fercProceedingViolation)) fail(FERC_PROCEEDING_MESSAGE);
  if ((row.status === 'unresolved') !== (row.unresolvedReason !== undefined)) fail('An unresolved reason is required exactly for unresolved rows.');
  if ((row.status === 'no-blocker-identified') !== (row.qualifier !== undefined)) fail('"No blocker identified" must carry the not-an-approval qualifier, and only that status may.');
  const evidenced = row.evidenceBasis === 'document' || row.evidenceBasis === 'provider';
  const effective = row.sources.some(source => source.ruleStatus === 'effective');
  if (row.status === 'confirmed-restriction' && !(evidenced && effective)) {
    fail('A confirmed restriction needs a document or provider basis with an effective rule.');
  }
  if (row.status === 'no-blocker-identified' && !(evidenced && effective)) {
    fail('"No blocker identified" needs a named, checked, effective document or provider source.');
  }
  if ((row.issue === 'air-permit-operating-duty') !== (row.permitStage !== undefined && row.duty !== undefined)) {
    fail('Permit stage and duty are carried by the air row only.');
  }
  if (new Set(row.affectedScenarios).size !== row.affectedScenarios.length
    || row.affectedScenarios.some((scenario, index) => index > 0
      && CONNECTION_SCENARIOS.indexOf(scenario) < CONNECTION_SCENARIOS.indexOf(row.affectedScenarios[index - 1]!))) {
    fail('Affected scenarios must be unique and in canonical order.');
  }
  if (ISLANDED_DOCTRINE_ISSUES.has(row.issue) && row.affectedScenarios.includes('islanded')
    && !row.implication.includes(REGULATORY_DOCTRINE.islandedNoExemption)) {
    fail('An islanded scenario must carry the no-exemption doctrine.');
  }
  if (row.issue === 'grid-interface' && row.affectedScenarios.includes('grid-parallel-non-export')) {
    if (row.status === 'no-blocker-identified') fail('Zero export never clears the grid interface.');
    if (!row.implication.includes(REGULATORY_DOCTRINE.zeroExportStillReviewed)) fail('A non-export row must state that interconnection review still applies.');
  }
  if (row.issue === 'air-permit-operating-duty' && (row.duty === 'emergency-standby' || row.duty === 'mixed')
    && !row.implication.includes(REGULATORY_DOCTRINE.emergencyNotPrime)) {
    fail('Emergency duty must state that emergency approval is not prime-power permission.');
  }
  for (const text of [row.implication, row.nextAction]) {
    for (const rule of FORBIDDEN_ISSUE_TEXT) if (rule.pattern.test(text)) fail(rule.message);
  }
});
export type RegulatoryIssue = z.infer<typeof regulatoryIssueShape>;

/**
 * Structural parser of a STORED v1 result: shape only, keyed by screen
 * version. Copy literals and doctrine are not re-checked, so a later copy
 * edit or a version bump never makes a stored report unreadable; the
 * record's hashes are its integrity.
 */
const storedRegulatoryScreenResultSchemaV1 = z.object({
  schemaVersion: z.literal(1),
  screenVersion: z.literal(REGULATORY_FEASIBILITY_SCREEN_VERSION_V1),
  sourcesCheckedOn: isoDate,
  reverifyAtReport: z.literal(true),
  qualifier: nonEmpty,
  noBlockerQualifier: nonEmpty,
  issues: z.array(regulatoryIssueShape).min(1),
}).strict();
export type RegulatoryFeasibilityScreenResult = z.infer<typeof storedRegulatoryScreenResultSchemaV1>;

/** Every screen version a stored record may carry, with its structural
 * parser. 'regulatory-feasibility-screen-v1' stays readable as legacy after a
 * version bump. */
export const STORED_REGULATORY_SCREEN_VERSIONS = [REGULATORY_FEASIBILITY_SCREEN_VERSION_V1] as const;
export type StoredRegulatoryScreenVersion = typeof STORED_REGULATORY_SCREEN_VERSIONS[number];
export const STORED_REGULATORY_SCREEN_RESULT_PARSERS: Readonly<Record<StoredRegulatoryScreenVersion, z.ZodType<RegulatoryFeasibilityScreenResult>>> = {
  [REGULATORY_FEASIBILITY_SCREEN_VERSION_V1]: storedRegulatoryScreenResultSchemaV1,
};

/** A NEWLY produced result: the live version and copy plus every doctrine
 * invariant. Stored records are read with STORED_REGULATORY_SCREEN_RESULT_PARSERS. */
export const regulatoryFeasibilityScreenResultSchema = z.object({
  schemaVersion: z.literal(1),
  screenVersion: z.literal(REGULATORY_FEASIBILITY_SCREEN_VERSION),
  sourcesCheckedOn: z.literal(SOURCES_CHECKED_ON),
  reverifyAtReport: z.literal(true),
  qualifier: z.literal(REGULATORY_SCREEN_QUALIFIER),
  noBlockerQualifier: z.literal(NO_BLOCKER_QUALIFIER),
  issues: z.array(regulatoryIssueSchema).min(REGULATORY_ISSUE_IDS.length),
}).strict().superRefine((result, context) => {
  const ids = result.issues.map(row => row.id);
  if (new Set(ids).size !== ids.length) context.addIssue({ code: 'custom', message: 'Regulatory issue row ids must be unique.' });
  for (const issue of REGULATORY_ISSUE_IDS) {
    const rows = result.issues.filter(row => row.issue === issue);
    if (rows.length === 0) context.addIssue({ code: 'custom', message: `Regulatory issue ${issue} is missing.` });
    if (issue !== 'grid-interface' && rows.length > 1) context.addIssue({ code: 'custom', message: `Regulatory issue ${issue} must have one row.` });
  }
  const interfaceScenarios = result.issues.filter(row => row.issue === 'grid-interface').flatMap(row => row.affectedScenarios);
  if (new Set(interfaceScenarios).size !== interfaceScenarios.length) {
    context.addIssue({ code: 'custom', message: 'Each connection scenario has exactly one grid-interface row.' });
  }
});

// ── Screen input ─────────────────────────────────────────────────────────────

/** Map a declared phase connection to its scenario. `exportMode` is
 * meaningful only for a grid-connected phase; islanded ignores it. */
export function connectionScenarioFor(
  gridConnection: PhaseInputGridConnection,
  exportMode: PhaseInputExportMode,
): ConnectionScenario {
  if (gridConnection === 'islanded') return 'islanded';
  if (gridConnection !== 'grid-connected') return 'unknown';
  if (exportMode === 'non-export') return 'grid-parallel-non-export';
  if (exportMode === 'export') return 'grid-parallel-export';
  return 'unknown';
}
export type PhaseInputGridConnection = 'islanded' | 'grid-connected' | 'undecided' | 'absent';
export type PhaseInputExportMode = 'non-export' | 'export' | 'unknown' | 'absent';

/** Components that burn fuel on site. */
export const COMBUSTION_COMPONENTS: ReadonlySet<string> = new Set(['recip', 'gas-turbine']);

const phaseInputSchema = z.object({
  phaseId: nonEmpty,
  sequence: z.number().int().positive(),
  gridConnectionDeclared: z.enum(['islanded', 'grid-connected', 'undecided', 'absent']),
  exportModeDeclared: z.enum(['non-export', 'export', 'unknown', 'absent']),
  connection: z.enum(CONNECTION_SCENARIOS),
  connectionRecordedUnknown: z.boolean(),
  /** Declared `powerSourceConfig.components`. */
  components: z.array(nonEmpty),
  combustion: z.boolean(),
  /** Any non-grid on-site generation or storage component. */
  generation: z.boolean(),
  duty: z.enum(GENERATION_DUTIES),
  dutyRecordedUnknown: z.boolean(),
  /** Informational only. No status ever depends on it (no MW safe harbour). */
  capacityMW: z.number().finite().nonnegative().nullable(),
}).strict().superRefine((phase, context) => {
  if (phase.connection !== connectionScenarioFor(phase.gridConnectionDeclared, phase.exportModeDeclared)
    || phase.combustion !== phase.components.some(component => COMBUSTION_COMPONENTS.has(component))
    || phase.generation !== phase.components.some(component => component !== 'grid')) {
    context.addIssue({ code: 'custom', message: `Phase ${phase.phaseId} input is internally inconsistent.` });
  }
});
export type RegulatoryPhaseInput = z.infer<typeof phaseInputSchema>;

const provenancedText = <T extends readonly [string, ...string[]]>(provenance: T) => z.object({
  value: nonEmpty.nullable(),
  provenance: z.enum(provenance),
}).strict().refine(value => (value.value === null) === (value.provenance === 'none'), 'A value needs a provenance and none needs no value.');

export const documentedRestrictionSchema = z.object({
  issue: z.enum(REGULATORY_ISSUE_IDS),
  basis: z.enum(EVIDENCE_BASES),
  authority: regulatoryAuthoritySchema,
  source: regulatorySourceSchema,
}).strict();
export type DocumentedRestriction = z.infer<typeof documentedRestrictionSchema>;

export const regulatoryFeasibilityInputSchema = z.object({
  schemaVersion: z.literal(1),
  jurisdiction: z.object({
    /** Two-letter state, with how it was resolved. A bounding-box estimate
     * stays 'estimate'; it never becomes an authority. */
    state: provenancedText(['provider', 'document', 'derived', 'estimate', 'none'] as const),
    localAuthority: z.object({
      /** Geographic candidate only (city limit / ETJ / place). */
      candidateName: nonEmpty.nullable(),
      screeningStatus: z.enum(['inside-city-limits', 'inside-etj', 'outside-known-etj', 'mixed', 'unknown', 'not-screened']),
      /** The jurisdiction screen never evaluates permitting authority. */
      authorityEvaluated: z.literal(false),
    }).strict(),
  }).strict(),
  landUse: z.object({
    parcelZoning: z.object({ value: nonEmpty.nullable(), provenance: z.enum(['provider', 'document', 'derived', 'none']), asOf: isoDate.nullable() }).strict(),
    parcelLandUse: z.object({ value: nonEmpty.nullable(), provenance: z.enum(['provider', 'document', 'derived', 'none']), asOf: isoDate.nullable() }).strict(),
    zoningDocument: z.object({
      /** 'document' = extracted from an uploaded document; 'declared' = user-authored. */
      tier: z.enum(['document', 'declared']),
      zoningCode: nonEmpty.nullable(),
      permittedUses: z.array(nonEmpty),
      setbacksFt: z.number().finite().nonnegative().nullable(),
      specialConditions: nonEmpty.nullable(),
    }).strict().nullable(),
    documentedRestrictions: z.array(documentedRestrictionSchema),
  }).strict(),
  air: z.object({
    epa: z.discriminatedUnion('status', [
      z.object({ status: z.literal('not-assessed') }).strict(),
      z.object({ status: z.literal('not-listed'), asOf: isoDate }).strict(),
      z.object({
        status: z.literal('nonattainment'),
        asOf: isoDate.nullable(),
        pollutant: z.enum(['ozone', 'pm2.5', 'pm10', 'unspecified']),
        classification: z.enum(['marginal', 'moderate', 'serious', 'severe', 'extreme']).nullable(),
        siteContainment: z.enum(['inside', 'outside', 'unknown', 'whole-county']),
      }).strict(),
    ]),
    ozoneTransportRegion: z.enum(['inside', 'outside', 'not-assessed']),
  }).strict(),
  utility: z.object({
    /** Free-text utility names declared on grid-connected phases. */
    declaredServingUtilities: z.array(nonEmpty),
    /** Utility named by a utility letter / document in the SED grid section.
     * Never the nearest-substation or transmission-line owner. */
    documentedServingUtility: z.object({ name: nonEmpty, asOf: isoDate.nullable() }).strict().nullable(),
    iso: provenancedText(['provider', 'none'] as const),
  }).strict(),
  phases: z.array(phaseInputSchema),
  fuel: z.object({
    supply: z.enum(['none', 'near-pipeline', 'lateral-required', 'firm-contracted', 'unknown']),
    recordedUnknown: z.boolean(),
  }).strict(),
  electricitySupply: z.object({
    arrangement: z.enum(['own-use', 'third-party-onsite-sale', 'utility-supplied', 'unknown']),
    sellerName: nonEmpty.nullable(),
    recordedUnknown: z.boolean(),
  }).strict(),
  gridService: z.object({
    capacity: z.object({
      basis: z.enum(['utility-indication', 'study', 'executed', 'none']),
      /** Informational only; never compared with a load. */
      mw: z.number().finite().nonnegative().nullable(),
      asOf: isoDate.nullable(),
      interconnectionClass: nonEmpty.nullable(),
    }).strict(),
    loadServiceStatus: z.object({
      value: z.enum(['none', 'pre-application', 'queued', 'study', 'executed-LGIA', 'executed-load-service', 'unknown']),
      recordedUnknown: z.boolean(),
    }).strict(),
  }).strict(),
}).strict().superRefine((input, context) => {
  const ids = input.phases.map(phase => phase.phaseId);
  if (new Set(ids).size !== ids.length) context.addIssue({ code: 'custom', message: 'Phase ids must be unique.' });
});
export type RegulatoryFeasibilityInput = z.infer<typeof regulatoryFeasibilityInputSchema>;
